
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import { useIsNewUser } from '@/hooks/useIsNewUser';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useQueryClientSetup } from '@/hooks/useQueryClientSetup';
import { loadMetaPixel, removeMetaPixel } from '@/utils/metaPixel';
import HeaderManager from './HeaderManager';
import { useShoppingMode } from '@/contexts/ShoppingModeContext';

import AppRoutes from './AppRoutes';
import MobileNavigation from './MobileNavigation';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import IOSInstallPrompt from './IOSInstallPrompt';

const AppContent: React.FC = () => {
  const { isAuthenticated, user } = useUser();
  const { isNewUser } = useIsNewUser();
  const { consent } = useCookieConsent();
  const location = useLocation();
  const { page } = useAnalytics();
  const { isShoppingMode } = useShoppingMode();
  
  // Set up global query client access for cache invalidation
  useQueryClientSetup();
  
  // Track page views
  useEffect(() => {
    const pageName = location.pathname === '/' ? 'landing' : location.pathname.slice(1).replace(/\//g, '_');
    page(pageName, {
      authenticated: isAuthenticated,
      is_new_user: isNewUser,
    });
  }, [location.pathname, isAuthenticated, isNewUser, page]);

  // Handle Meta Pixel loading based on consent
  useEffect(() => {
    if (consent) {
      if (consent.marketing) {
        loadMetaPixel(consent);
      } else {
        removeMetaPixel();
      }
    }
  }, [consent]);

  // Track page leaves on browser close/navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable delivery on page unload
      if (navigator.sendBeacon) {
        // This will be handled by the existing analytics queue system
        // which will flush any pending events
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {!isShoppingMode && <HeaderManager />}
      <div className="flex-1">
        <AppRoutes />
      </div>
      {!isShoppingMode && <MobileNavigation />}
      {!isShoppingMode && <Footer />}
      <IOSInstallPrompt />
      <CookieBanner />
    </div>
  );
};

export default AppContent;
